import type { Station } from '@/types/domain';
import { haversineKm } from '@/utils/geo';
import { SMART_TUNE } from '@/config/product.config';

/**
 * Pure, unit-testable Smart Tune scoring function.
 *
 * Returns a numeric score for a station relative to the current map
 * context. Higher is better. All sub-signals are normalized to [0,1]
 * and combined with weights, then the result is scaled to [0,1].
 */

export interface ScoreContext {
  /** [lng, lat] map center */
  mapCenter: [number, number];
  zoom: number;
  /** epoch ms */
  now: number;
  currentStationId: string | null;
}

const WEIGHTS = {
  proximity: 0.28,
  clickCount: 0.2,
  clickTrend: 0.12,
  votes: 0.1,
  health: 0.16,
  https: 0.06,
  bitrate: 0.05,
  freshness: 0.03,
} as const;

const clamp01 = (x: number): number => Math.max(0, Math.min(1, x));
const logNorm = (x: number): number => (x > 0 ? Math.log10(x + 1) / 6 : 0);

export function scoreStation(station: Station, ctx: ScoreContext): number {
  const { mapCenter, now } = ctx;

  // proximity — exponential decay from map center
  let proximity = 0;
  if (station.location) {
    const km = haversineKm(mapCenter, station.location);
    // decay: ~1 at 0km, ~0.1 at ~3000km
    proximity = clamp01(Math.exp(-km / 900));
  }

  const clickCount = logNorm(station.clickCount);
  // trend can be negative; map [-50,50] -> [0,1]
  const clickTrend = clamp01((station.clickTrend + 50) / 100);
  const votes = logNorm(station.votes);

  // health — working stream, with freshness multiplier
  const stream = station.streams[0];
  const working = stream?.isWorking ? 1 : 0.2;
  const freshnessRatio = clamp01(1 - (now - 0) / (1000 * 60 * 60 * 24 * 30));
  const health = working * freshnessRatio;

  const https = stream?.isHttps ? 1 : 0.4;
  const bitrate = clamp01((stream?.bitrate ?? 0) / 320);

  // metadata freshness — stations with no location score lower
  const freshness = station.location ? 1 : 0.5;

  return clamp01(
    proximity * WEIGHTS.proximity +
      clickCount * WEIGHTS.clickCount +
      clickTrend * WEIGHTS.clickTrend +
      votes * WEIGHTS.votes +
      health * WEIGHTS.health +
      https * WEIGHTS.https +
      bitrate * WEIGHTS.bitrate +
      freshness * WEIGHTS.freshness,
  );
}

/**
 * Rank candidates and decide whether to switch.
 * Returns the chosen station, or null if the current station should stay.
 */
export function rankAndPick(
  candidates: Station[],
  ctx: ScoreContext,
): { station: Station | null; reason: 'keep' | 'switch' | 'none' } {
  if (candidates.length === 0) return { station: null, reason: 'none' };

  const ranked = candidates
    .map((s) => ({ station: s, score: scoreStation(s, ctx) }))
    .sort((a, b) => b.score - a.score);

  const top = ranked[0];
  if (!top) return { station: null, reason: 'none' };

  // stability: keep current station if it remains in the top fraction
  if (ctx.currentStationId) {
    const keepCount = Math.max(
      1,
      Math.ceil(ranked.length * SMART_TUNE.currentStationKeepFraction),
    );
    const topIds = ranked.slice(0, keepCount).map((r) => r.station.id);
    if (topIds.includes(ctx.currentStationId)) {
      const current = candidates.find((s) => s.id === ctx.currentStationId);
      if (current) return { station: current, reason: 'keep' };
    }
  }

  return { station: top.station, reason: 'switch' };
}
